import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  PlayCircle,
  Tag,
  X,
  Loader2,
  Film,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Toaster, toast } from "@/components/ui/sonner";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/contexts/AuthContext";

const API = `${window.location.origin}/api`;

// Local card displayed in the grid. Click → opens the player modal.
function VideoCard({ video, isAdmin, onPlay, onEdit, onDelete }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-indigo-300 hover:shadow-md transition-all flex flex-col"
      data-testid={`video-card-${video.id}`}
    >
      <button
        type="button"
        onClick={() => onPlay(video)}
        className="relative aspect-video bg-slate-100 overflow-hidden text-left"
        data-testid={`play-${video.id}`}
        aria-label={`Riproduci ${video.custom_title}`}
      >
        {video.thumbnail_url && !imgError ? (
          <img
            src={video.thumbnail_url}
            alt={video.custom_title}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
            <Film className="w-12 h-12 text-slate-400" />
          </div>
        )}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
          <PlayCircle className="w-14 h-14 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-lg" />
        </div>
        <span className="absolute top-2 left-2 text-[10px] font-bold uppercase tracking-wider bg-white/90 text-slate-700 px-2 py-0.5 rounded-md">
          {video.platform === "loom" ? "Loom" : "YouTube"}
        </span>
      </button>

      <div className="p-4 flex flex-col gap-2 flex-1">
        <h3 className="text-sm font-bold text-slate-800 line-clamp-2" data-testid={`title-${video.id}`}>
          {video.custom_title}
        </h3>
        {video.argomento && (
          <div className="text-xs text-indigo-700 font-semibold flex items-center gap-1">
            <Tag className="w-3 h-3" /> {video.argomento}
          </div>
        )}
        {video.keywords && video.keywords.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {video.keywords.map((k) => (
              <span
                key={k}
                className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
              >
                {k}
              </span>
            ))}
          </div>
        )}

        {isAdmin && (
          <div className="mt-auto pt-2 flex items-center gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => onEdit(video)}
              className="text-xs flex items-center gap-1 text-slate-600 hover:text-indigo-600"
              data-testid={`edit-${video.id}`}
            >
              <Pencil className="w-3.5 h-3.5" /> Modifica
            </button>
            <button
              type="button"
              onClick={() => onDelete(video)}
              className="text-xs flex items-center gap-1 text-slate-600 hover:text-rose-600 ml-auto"
              data-testid={`delete-${video.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" /> Elimina
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Admin form for creating / editing a video tutorial.
function VideoFormDialog({ open, onOpenChange, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [videoUrl, setVideoUrl] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [argomento, setArgomento] = useState("");
  const [keywordsInput, setKeywordsInput] = useState("");
  const [keywords, setKeywords] = useState([]);
  const [saving, setSaving] = useState(false);
  // Copertina personalizzata (sostituisce thumbnail YouTube/Loom).
  // `coverFile` è il File selezionato in attesa di upload (POST separata
  // dopo create/update). `coverPreviewUrl` è data-URL per anteprima
  // immediata in form (sia per file appena selezionato sia per copertina
  // già caricata sul server). `removeCover` chiede al SAVE di cancellare
  // la cover lato server. Schema: la POST `/cover` ritorna l'oggetto
  // video aggiornato con `cover_image_data_url` salvato inline.
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = useState("");
  const [removeCover, setRemoveCover] = useState(false);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setVideoUrl(initial?.video_url || "");
      setCustomTitle(initial?.custom_title || "");
      setArgomento(initial?.argomento || "");
      setKeywords(initial?.keywords || []);
      setKeywordsInput("");
      setCoverFile(null);
      setRemoveCover(false);
      setCoverPreviewUrl(initial?.cover_image_data_url || "");
    }
  }, [open, initial]);

  const onCoverChange = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Carica un file immagine (PNG/JPG/WEBP/GIF)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`File troppo grande (${(file.size / 1024 / 1024).toFixed(1)} MB > 5 MB)`);
      return;
    }
    setCoverFile(file);
    setRemoveCover(false);
    const reader = new FileReader();
    reader.onload = () => setCoverPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const clearCover = () => {
    setCoverFile(null);
    setCoverPreviewUrl("");
    setRemoveCover(!!initial?.cover_image_data_url);
    if (coverInputRef.current) coverInputRef.current.value = "";
  };

  const addKeyword = (raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    if (keywords.some((k) => k.toLowerCase() === v.toLowerCase())) return;
    setKeywords([...keywords, v]);
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword(keywordsInput);
      setKeywordsInput("");
    } else if (e.key === "Backspace" && !keywordsInput && keywords.length > 0) {
      setKeywords(keywords.slice(0, -1));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim() || !customTitle.trim()) {
      toast.error("URL video e titolo sono obbligatori");
      return;
    }
    setSaving(true);
    const payload = {
      video_url: videoUrl.trim(),
      custom_title: customTitle.trim(),
      argomento: argomento.trim(),
      keywords,
    };
    try {
      let savedVideo;
      if (isEdit) {
        const { data } = await axios.put(`${API}/video-tutorials/${initial.id}`, payload, { withCredentials: true });
        savedVideo = data;
      } else {
        const { data } = await axios.post(`${API}/video-tutorials`, payload, { withCredentials: true });
        savedVideo = data;
      }
      // Cover image: upload se nuovo file scelto; delete se l'utente ha
      // rimosso una cover esistente. Le 2 azioni sono mutually exclusive
      // (clearCover resetta coverFile a null e setta removeCover=true
      // solo se l'iniziale aveva una cover).
      if (coverFile && savedVideo?.id) {
        const fd = new FormData();
        fd.append("file", coverFile);
        try {
          await axios.post(`${API}/video-tutorials/${savedVideo.id}/cover`, fd, {
            withCredentials: true,
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (covErr) {
          toast.error(covErr.response?.data?.detail || "Errore upload copertina");
        }
      } else if (removeCover && savedVideo?.id) {
        try {
          await axios.delete(`${API}/video-tutorials/${savedVideo.id}/cover`, { withCredentials: true });
        } catch (delErr) {
          toast.error(delErr.response?.data?.detail || "Errore rimozione copertina");
        }
      }
      toast.success(isEdit ? "Video aggiornato" : "Video aggiunto");
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="video-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Modifica video tutorial" : "Aggiungi video tutorial"}</DialogTitle>
          <DialogDescription>
            Incolla un link YouTube o Loom. Imposta un titolo personalizzato, un argomento e alcune parole chiave per facilitare la ricerca.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="video-url">URL video (YouTube / Loom) *</Label>
            <Input
              id="video-url"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=... oppure https://www.loom.com/share/..."
              required
              data-testid="input-video-url"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="custom-title">Titolo personalizzato *</Label>
            <Input
              id="custom-title"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Es: Come compilare un piano di cura"
              required
              data-testid="input-custom-title"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="argomento">Argomento</Label>
            <Input
              id="argomento"
              value={argomento}
              onChange={(e) => setArgomento(e.target.value)}
              placeholder="Es: Piani di cura"
              data-testid="input-argomento"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="keywords">Parole chiave</Label>
            <div className="flex flex-wrap gap-1.5 p-2 rounded-md border border-slate-200 bg-white min-h-[44px]">
              {keywords.map((k) => (
                <Badge
                  key={k}
                  variant="secondary"
                  className="gap-1"
                  data-testid={`form-keyword-${k}`}
                >
                  {k}
                  <button
                    type="button"
                    onClick={() => setKeywords(keywords.filter((x) => x !== k))}
                    className="hover:text-rose-600"
                    aria-label={`Rimuovi ${k}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
              <input
                id="keywords"
                value={keywordsInput}
                onChange={(e) => setKeywordsInput(e.target.value)}
                onKeyDown={handleKeywordKeyDown}
                onBlur={() => {
                  if (keywordsInput.trim()) {
                    addKeyword(keywordsInput);
                    setKeywordsInput("");
                  }
                }}
                placeholder={keywords.length === 0 ? "Premi Invio o virgola per aggiungere" : ""}
                className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                data-testid="input-keyword"
              />
            </div>
            <p className="text-[11px] text-slate-500">
              Premi <kbd className="px-1 bg-slate-100 rounded">Invio</kbd> o <kbd className="px-1 bg-slate-100 rounded">,</kbd> per aggiungere una parola chiave.
            </p>
          </div>

          {/* Cover image: opzionale. Se caricata SOSTITUISCE la thumbnail
              YouTube/Loom autogenerata. Storage inline base64 nel doc
              Mongo (max 5MB). */}
          <div>
            <label className="block text-sm font-medium mb-1">Copertina personalizzata <span className="text-xs text-slate-400 font-normal">(opzionale · max 5 MB)</span></label>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              onChange={(e) => onCoverChange(e.target.files?.[0])}
              className="hidden"
              data-testid="input-cover-file"
            />
            {coverPreviewUrl ? (
              <div className="relative group">
                <img
                  src={coverPreviewUrl}
                  alt="Anteprima copertina"
                  className="w-full max-h-48 object-contain rounded-lg border border-slate-200 bg-slate-50"
                  data-testid="cover-preview-img"
                />
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => coverInputRef.current?.click()}
                    disabled={saving}
                    data-testid="btn-replace-cover"
                  >
                    Sostituisci
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={clearCover}
                    disabled={saving}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid="btn-remove-cover"
                  >
                    Rimuovi
                  </Button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => coverInputRef.current?.click()}
                disabled={saving}
                className="w-full border-2 border-dashed border-slate-300 rounded-lg py-6 px-4 text-center hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors"
                data-testid="btn-upload-cover"
              >
                <p className="text-sm text-slate-600">📷 Clicca per caricare un&apos;immagine</p>
                <p className="text-[11px] text-slate-400 mt-1">PNG, JPG, WEBP o GIF · max 5 MB</p>
              </button>
            )}
            <p className="text-[11px] text-slate-500 mt-1">
              Se carichi una copertina personalizzata, sostituirà la miniatura YouTube/Loom autogenerata.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-video">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEdit ? "Salva modifiche" : "Aggiungi video"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Embedded player modal (YouTube/Loom iframe).
function PlayerDialog({ video, onOpenChange }) {
  const open = !!video;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onOpenChange(null)}>
      <DialogContent className="max-w-5xl p-0 overflow-hidden" data-testid="player-dialog">
        <DialogHeader className="px-6 pt-5 pb-2">
          <DialogTitle className="text-base">{video?.custom_title || ""}</DialogTitle>
          {video?.argomento && (
            <DialogDescription className="text-xs text-indigo-700 font-semibold flex items-center gap-1">
              <Tag className="w-3 h-3" /> {video.argomento}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="relative bg-black" style={{ paddingTop: "56.25%" }}>
          {video && (
            <iframe
              src={video.embed_url}
              title={video.custom_title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="player-iframe"
            />
          )}
        </div>
        {video?.keywords && video.keywords.length > 0 && (
          <div className="px-6 py-3 flex flex-wrap gap-1 border-t border-slate-100">
            {video.keywords.map((k) => (
              <span key={k} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                {k}
              </span>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function VideoTutorialPage() {
  const { user } = useAuth();
  const isAdmin = (user?.role || "") === "admin";

  const [videos, setVideos] = useState([]);
  const [keywordsAll, setKeywordsAll] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [selectedKeywords, setSelectedKeywords] = useState([]);

  const [playing, setPlaying] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, kRes] = await Promise.all([
        axios.get(`${API}/video-tutorials`, { withCredentials: true }),
        axios.get(`${API}/video-tutorials/keywords`, { withCredentials: true }),
      ]);
      setVideos(vRes.data?.items || []);
      setKeywordsAll(kRes.data?.keywords || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento video");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredVideos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return videos.filter((v) => {
      // Keyword filter (AND across selected keywords).
      if (selectedKeywords.length > 0) {
        const vkw = (v.keywords || []).map((k) => k.toLowerCase());
        const ok = selectedKeywords.every((k) => vkw.includes(k.toLowerCase()));
        if (!ok) return false;
      }
      if (q) {
        const hay = [
          v.custom_title || "",
          v.argomento || "",
          ...(v.keywords || []),
        ].join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [videos, search, selectedKeywords]);

  const toggleKeyword = (k) => {
    setSelectedKeywords((prev) =>
      prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]
    );
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await axios.delete(`${API}/video-tutorials/${pendingDelete.id}`, { withCredentials: true });
      toast.success("Video eliminato");
      setPendingDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore eliminazione");
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <Toaster />
      <div className="w-full">
        <PageHeader
          title="Video Tutorial"
          subtitle="Guarda i video formativi dello studio. Usa la ricerca o le parole chiave per trovare velocemente quello che ti serve."
          actions={
            isAdmin && (
              <Button
                type="button"
                onClick={() => { setEditing(null); setFormOpen(true); }}
                data-testid="btn-add-video"
                className="bg-white/15 hover:bg-white/25 border border-white/30 text-white backdrop-blur-sm"
              >
                <Plus className="w-4 h-4 mr-2" /> Aggiungi video
              </Button>
            )
          }
        />
      </div>

      {/* Filter bar */}
      <div className="w-full mb-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per titolo, argomento o parola chiave..."
              className="pl-9"
              data-testid="input-search"
            />
          </div>
          {keywordsAll.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mr-1 self-center">
                Parole chiave
              </span>
              {keywordsAll.map((k) => {
                const active = selectedKeywords.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleKeyword(k)}
                    data-testid={`filter-keyword-${k}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                      active
                        ? "bg-indigo-600 text-white border-indigo-600"
                        : "bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
                    }`}
                  >
                    {k}
                  </button>
                );
              })}
              {selectedKeywords.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedKeywords([])}
                  className="text-xs px-2 py-1 text-rose-600 hover:text-rose-700"
                  data-testid="clear-keywords"
                >
                  Azzera filtri
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="w-full">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Caricamento video...
          </div>
        ) : filteredVideos.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-slate-300 py-16 text-center text-slate-500">
            <Film className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            {videos.length === 0
              ? (isAdmin
                  ? "Nessun video ancora. Clicca \"Aggiungi video\" per iniziare."
                  : "Nessun video tutorial disponibile.")
              : "Nessun video corrisponde ai filtri."}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="videos-grid">
            {filteredVideos.map((v) => (
              <VideoCard
                key={v.id}
                video={v}
                isAdmin={isAdmin}
                onPlay={setPlaying}
                onEdit={(video) => { setEditing(video); setFormOpen(true); }}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
        )}
      </div>

      <PlayerDialog video={playing} onOpenChange={setPlaying} />

      <VideoFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={editing}
        onSaved={fetchData}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(v) => !v && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il video?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingDelete?.custom_title}" sarà rimosso definitivamente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-video"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
